import type { TreeCursor } from '@lezer/common';

import type { Operator, ParsedToken } from '../../types/QueryBuilder';

import { parser } from './generated/parser';
// eslint-disable-next-line import/no-namespace
import * as Terms from './generated/parser.terms';

// ─── Filter re-parsing ────────────────────────────────────────────────────────
//
// The Lezer grammar represents the complete filter (field:op:value) as a single
// opaque token to avoid LR tokenisation conflicts. We re-parse the token text
// with this regex to extract the structured fields.
//
// Operators ordered longest-first so multi-char ops are preferred over shorter prefixes:
// "!//" over "//", ">=" over ">", "<=" over "<", "!=" / "=^" over "=".

// Single source of truth for the operator alternation — kept in sync with the `Operator`
// union via the `OPERATORS` array, so a regex-vs-type drift is caught by the type checker
// rather than a silent `as` cast.
const OPERATORS = [
  '**',
  '!//',
  '//',
  '>=',
  '<=',
  '!=',
  '^=',
  '=^',
  '>',
  '<',
  '=',
] as const satisfies readonly Operator[];

/**
 * Operator alternation source — exported so syntax-highlighting consumers (e.g.
 * the Storybook playground's CodeMirror plugin) can reuse the exact same set of
 * operators without redefining them and silently drifting from this module.
 */
export const OPERATOR_RE_SRC = OPERATORS.map((op) => op.replace(/[*^]/g, '\\$&')).join('|');

function isOperator(s: string): s is Operator {
  return (OPERATORS as readonly string[]).includes(s);
}

/**
 * Key alternation source: either a double-quoted string (`"..."`) or bare
 * identifier segments (dot-separated). Exported for the same reason as
 * `OPERATOR_RE_SRC`.
 */
export const KEY_RE_SRC = '(?:"[^"]*"|[a-zA-Z0-9_\\-]+(?:\\.[a-zA-Z0-9_\\-]+)*)';

/**
 * Single filter-value alternation source: either a double-quoted string or bare
 * non-whitespace chars (excluding comma and quote so bare values stay
 * unambiguous). Exported for syntax-highlighting reuse.
 */
export const FILTER_VAL_RE_SRC = '(?:"[^"]*"|[^\\s,:"]+)';

const FILTER_RE = new RegExp(
  `^(!?)(${KEY_RE_SRC}):(${OPERATOR_RE_SRC}):(${FILTER_VAL_RE_SRC}(?:,${FILTER_VAL_RE_SRC})*),?$`,
);

/** Strip surrounding double-quotes from a string, if present. */
function unquote(s: string): string {
  return s.length >= 2 && s.startsWith('"') && s.endsWith('"') ? s.slice(1, -1) : s;
}

/** Coerce a single bare-value string to the most specific primitive type. */
function coerceScalar(raw: string): string | number | boolean {
  if (raw === 'true') {
    return true;
  }
  if (raw === 'false') {
    return false;
  }
  const n = Number(raw);
  return raw !== '' && Number.isFinite(n) ? n : raw;
}

/**
 * Parse the raw filter-value section into a scalar or array.
 * Handles both quoted values ("hello world") and bare values (hello),
 * splitting on commas that appear outside quoted strings.
 */
function parseFilterValues(raw: string): string | number | boolean | Array<string | number | boolean> {
  const parts: Array<string | number | boolean> = [];
  let i = 0;
  let current = '';

  while (i < raw.length) {
    if (raw[i] === '"') {
      // Consume until the closing quote (no escape sequences supported).
      const closing = raw.indexOf('"', i + 1);
      if (closing === -1) {
        current += raw.slice(i); // unclosed quote — treat remainder literally
        i = raw.length;
      } else {
        current += raw.slice(i + 1, closing); // content without the quotes
        i = closing + 1;
      }
    } else if (raw[i] === ',') {
      parts.push(coerceScalar(current));
      current = '';
      i++;
    } else {
      current += raw[i++];
    }
  }

  // Push the final segment (trailing comma already stripped by FILTER_RE `,?$`).
  parts.push(coerceScalar(current));

  return parts.length === 1 ? parts[0] : parts;
}

// ─── Free-text collapse ───────────────────────────────────────────────────────

/**
 * Merge adjacent FREE_TEXT tokens into one, extending the span to cover the
 * whitespace between them so that `token.text === query.slice(start, end)`.
 *
 * Lezer emits one Term per whitespace-delimited word, so "hello world" produces
 * two FREE_TEXT entries. This step collapses them back into one.
 */
function collapseAdjacentFreeText(tokens: ParsedToken[], query: string): ParsedToken[] {
  const result: ParsedToken[] = [];

  for (const token of tokens) {
    const last = result[result.length - 1];

    if (token.type === 'FREE_TEXT' && last?.type === 'FREE_TEXT') {
      const newEnd = token.location.end.offset;
      const merged = query.slice(last.location.start.offset, newEnd);
      last.text = merged;
      last.value = merged;
      last.location = { start: last.location.start, end: { offset: newEnd } };
    } else {
      result.push(token);
    }
  }

  return result;
}

// ─── Single-term parsing ──────────────────────────────────────────────────────

/**
 * Parse a single term node. The cursor must be positioned at the FIRST CHILD
 * of a Term node (i.e. after calling `cursor.firstChild()` on a Term).
 *
 * Returns the parsed token, or null for unknown/error nodes.
 * Does NOT move the cursor — callers are responsible for `cursor.parent()`.
 */
function parseSingleTermChild(cursor: TreeCursor, query: string): ParsedToken | null {
  const typeId: number = cursor.type.id;
  const from = cursor.from;
  const to = cursor.to;
  const text = query.slice(from, to);

  switch (typeId) {
    case Terms.Filter: {
      const m = FILTER_RE.exec(text);
      if (m) {
        const [, negStr, rawField, rawOperator, rawValue] = m;
        if (isOperator(rawOperator)) {
          return {
            type: 'FILTER',
            field: unquote(rawField),
            operator: rawOperator,
            value: parseFilterValues(rawValue),
            ...(negStr ? { negated: true } : {}),
            text,
            location: { start: { offset: from }, end: { offset: to } },
          };
        }
      }
      // Grammar matched Filter but regex disagrees — treat as free text.
      return {
        type: 'FREE_TEXT',
        value: text,
        text,
        location: { start: { offset: from }, end: { offset: to } },
      };
    }

    case Terms.FreeText: {
      // Boolean operator keywords (AND, OR) are tokenised as FreeText — detect
      // them by exact text comparison (see grammar tokenisation notes).
      if (text === 'AND' || text === 'OR') {
        return {
          type: 'LOGIC_BOOL',
          value: text,
          text,
          location: { start: { offset: from }, end: { offset: to } },
        };
      }
      return {
        type: 'FREE_TEXT',
        value: text,
        text,
        location: { start: { offset: from }, end: { offset: to } },
      };
    }

    case Terms.Expr: {
      const { tokens: inner, expressionType } = parseExprChildren(cursor, query);
      return {
        type: 'EXPRESSION',
        inner: collapseAdjacentFreeText(inner, query),
        text,
        location: { start: { offset: from }, end: { offset: to } },
        ...(expressionType !== undefined ? { expressionType } : {}),
      };
    }

    default:
      // Error nodes (type 0) and other unexpected nodes are silently ignored.
      return null;
  }
}

/**
 * Walk the children of an Expr node, collecting tokens from each nested Term and
 * recovering the optional `expressionType` from a leading ExprPrefix child.
 *
 * The cursor must be positioned AT the Expr node when called.
 * After this function returns the cursor is back at the Expr node.
 */
function parseExprChildren(
  cursor: TreeCursor,
  query: string,
): { tokens: ParsedToken[]; expressionType: string | undefined } {
  const tokens: ParsedToken[] = [];
  let expressionType: string | undefined;

  // Enter Expr's children: (ExprPrefix | "(") Ws? Term* ")"
  if (!cursor.firstChild()) {
    return { tokens, expressionType };
  }

  do {
    // ExprPrefix's text is `<identifier>(` (the trailing "(" was consumed by the
    // prefix token in the grammar so the lexer reliably picks it over FreeText).
    if (cursor.type.id === Terms.ExprPrefix) {
      const prefixText = query.slice(cursor.from, cursor.to);
      expressionType = prefixText.endsWith('(') ? prefixText.slice(0, -1) : prefixText;
      continue;
    }

    // Skip "(" ")" inline tokens and Ws — only process Term nodes.
    if (cursor.type.id !== Terms.Term) {
      continue;
    }

    if (!cursor.firstChild()) {
      continue;
    }

    const token = parseSingleTermChild(cursor, query);
    if (token) {
      tokens.push(token);
    }

    cursor.parent(); // back to Term
  } while (cursor.nextSibling());

  cursor.parent(); // back to Expr

  return { tokens, expressionType };
}

// ─── Main entry point ─────────────────────────────────────────────────────────

/**
 * Parse a raw query string into a list of typed tokens using the compiled
 * Lezer parser generated from `grammar.lezer`.
 *
 * The Lezer tree has the shape:
 *   Search → (Ws | Term)*
 *   Term   → (Expr | Filter | FreeText) Ws?
 *   Expr   → "(" (Ws | Term)* ")"
 *
 * Adjacent FREE_TEXT tokens are collapsed into a single token whose `.text`
 * and `.value` span the full range including internal whitespace.
 */
export function parseQuery(query: string): ParsedToken[] {
  if (!query.trim()) {
    return [];
  }

  const tree = parser.parse(query);
  const tokens: ParsedToken[] = [];
  const cursor = tree.cursor();

  // Descend into Search's children (Ws? Term*).
  if (!cursor.firstChild()) {
    return [];
  }

  do {
    // Skip leading Ws and any unexpected non-Term nodes.
    if (cursor.type.id !== Terms.Term) {
      continue;
    }

    // Enter the Term to reach its first child: Expr | Filter | FreeText.
    if (!cursor.firstChild()) {
      continue;
    }

    const token = parseSingleTermChild(cursor, query);
    if (token) {
      tokens.push(token);
    }

    // Return to the Term level so nextSibling() advances within Search.
    cursor.parent();
  } while (cursor.nextSibling());

  return collapseAdjacentFreeText(tokens, query);
}

/** Derive a stable React list key for a parsed token. */
export function tokenKey(token: ParsedToken, index: number): string {
  return `${token.type}:${index}`;
}
